import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

/**
 * Canonical category → routing inbox map.
 * Mirrors the user-facing copy on /support and the form picker.
 */
export const SUPPORT_CATEGORIES = [
  "Account",
  "Technical",
  "Product",
  "Billing",
  "Refund",
  "Subscription",
  "Legal",
  "Compliance",
  "Privacy",
  "Terms",
  "Other",
] as const;

export type SupportCategory = (typeof SUPPORT_CATEGORIES)[number];

export function routeSupportCategory(c: SupportCategory): string {
  switch (c) {
    case "Billing":
    case "Refund":
    case "Subscription":
      return "billing@forexmind.ai";
    case "Legal":
    case "Compliance":
    case "Privacy":
    case "Terms":
      return "legal@forexmind.ai";
    default:
      return "support@forexmind.ai";
  }
}

const SupportSchema = z.object({
  name: z.string().trim().min(1).max(120),
  email: z.string().trim().email().max(255),
  category: z.enum(SUPPORT_CATEGORIES),
  message: z.string().trim().min(5).max(4000),
});

export const submitSupportRequest = createServerFn({ method: "POST" })
  .inputValidator((i) => SupportSchema.parse(i))
  .handler(async ({ data }) => {
    const routedTo = routeSupportCategory(data.category);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    await supabaseAdmin
      .from("audit_log")
      .insert({
        actor_email: data.email,
        action: "support_request_submitted",
        target_type: "support",
        metadata: {
          name: data.name,
          category: data.category,
          routed_to: routedTo,
          message: data.message,
        },
      })
      .then(() => undefined, () => undefined);
    return { ok: true, routedTo };
  });
